import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2, Calendar as CalendarIcon, Image as ImageIcon, Award, Shield, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProject, useDeleteProject } from "@/hooks/api/use-projects";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { ProtectedImage } from "@/hooks/use-protected-image";

const ProjectDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const isApproved = user?.client?.status === 'approved';
  
  const projectId = id ? parseInt(id, 10) : 0;
  const { data, isLoading, error } = useProject(projectId);
  const deleteProject = useDeleteProject();

  const project = data?.data;

  const handleDelete = () => {
    if (confirm(t('portfolio.confirmDelete') || 'هل أنت متأكد من حذف هذا المشروع؟')) {
      deleteProject.mutate(projectId, {
        onSuccess: () => {
          navigate('/dashboard/portfolio');
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-8">
          <div className="space-y-6">
            <Skeleton className="h-10 w-64" />
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <Skeleton className="h-96 w-full" />
              <div className="p-6 space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <AlertCircle size={40} className="text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              {t('portfolio.projectNotFound') || 'المشروع غير موجود'}
            </h3>
            <p className="text-slate-600 mb-8">
              {t('portfolio.projectNotFoundDesc') || 'لم يتم العثور على المشروع المطلوب أو قد تم حذفه'}
            </p>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard/portfolio')} 
              className="gap-2 bg-white border-slate-200 hover:bg-slate-50"
            >
              <ArrowLeft size={18} />
              {t('common.back') || 'رجوع'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const title = typeof project.title === 'string' 
    ? project.title 
    : project.title[language] || project.title.en;
  const description = project.description 
    ? (typeof project.description === 'string' 
      ? project.description 
      : project.description[language] || project.description.en)
    : '';
  const location = project.location
    ? (typeof project.location === 'string'
      ? project.location
      : project.location[language] || project.location.en)
    : '';
  
  const completedDate = project.completed_date 
    ? format(new Date(project.completed_date), "PPP", { locale: isRTL ? ar : enUS })
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10">
        {/* Enhanced Header */}
        <div className="bg-white/50 backdrop-blur-sm border-b border-slate-200">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/dashboard/portfolio')} 
                  className="gap-2 bg-white border-slate-200 hover:bg-slate-50"
                >
                  <ArrowLeft size={18} />
                  {t('common.back') || 'رجوع'}
                </Button>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Award size={20} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-slate-900">
                      {t('portfolio.projectDetails') || 'تفاصيل المشروع'}
                    </h1>
                    <p className="text-sm text-slate-600">
                      {t('portfolio.viewingProject') || 'عرض المشروع'}
                    </p>
                  </div>
                </div>
              </div>
              
              {isApproved && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard/portfolio', { state: { editProjectId: projectId } })}
                    className="gap-2 bg-white border-slate-200 hover:bg-slate-50"
                  >
                    <Edit size={18} />
                    {t('portfolio.edit') || 'تعديل'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleteProject.isPending}
                    className="gap-2"
                  >
                    <Trash2 size={18} />
                    {t('portfolio.delete') || 'حذف'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Project Card */}
              <Card className="bg-white border-slate-200 shadow-xl overflow-hidden rounded-2xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                  <div className="flex items-start justify-between">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                          <Award size={24} className="text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl text-slate-900">{title}</CardTitle>
                          {project.status && (
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              project.status === 'approved'
                                ? 'bg-green-100 text-green-700'
                                : project.status === 'rejected'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {project.status === 'approved'
                                ? t('common.approved') || 'معتمد'
                                : project.status === 'rejected'
                                ? t('common.rejected') || 'مرفوض'
                                : t('common.pending') || 'قيد المراجعة'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Quick Info */}
                      <div className="flex items-center gap-6 text-sm text-slate-600">
                        {location && (
                          <div className="flex items-center gap-1">
                            <span>📍</span>
                            <span>{location}</span>
                          </div>
                        )}
                        {completedDate && (
                          <div className="flex items-center gap-1">
                            <CalendarIcon size={16} />
                            <span>{completedDate}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6 space-y-6">
                  {/* Description */}
                  {description && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Sparkles size={16} className="text-primary" />
                        </div>
                        {t('portfolio.description') || 'وصف المشروع'}
                      </h3>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-slate-700 leading-relaxed">{description}</p>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {location && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Shield size={16} className="text-primary" />
                        </div>
                        {t('portfolio.locationAr') || 'الموقع'}
                      </h3>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-slate-700">{location}</p>
                      </div>
                    </div>
                  )}

                  {/* Completed Date */}
                  {completedDate && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <CalendarIcon size={16} className="text-primary" />
                        </div>
                        {t('portfolio.completedDate') || 'تاريخ الإنجاز'}
                      </h3>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-slate-700 flex items-center gap-2">
                          <CalendarIcon size={18} className="text-primary" />
                          {completedDate}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  {project.status && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Shield size={16} className="text-primary" />
                        </div>
                        {t('common.status') || 'الحالة'}
                      </h3>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <span
                          className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                            project.status === 'approved'
                              ? 'bg-green-100 text-green-700'
                              : project.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {project.status === 'approved'
                            ? t('common.approved') || 'معتمد'
                            : project.status === 'rejected'
                            ? t('common.rejected') || 'مرفوض'
                            : t('common.pending') || 'قيد المراجعة'}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Image Gallery */}
              {project.images && project.images.length > 0 && (
                <Card className="bg-white border-slate-200 shadow-xl rounded-2xl overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <ImageIcon size={16} className="text-primary" />
                      </div>
                      {t('portfolio.projectImages') || 'صور المشروع'}
                    </h3>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {project.images.map((image, index) => (
                        <div key={image.id} className="group relative">
                          <div className="aspect-square rounded-xl overflow-hidden border border-slate-200">
                            <ProtectedImage
                              src={image.url}
                              alt={`${title} - Image ${index + 1}`}
                              containerClassName="w-full h-full"
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            />
                          </div>
                          <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                            {index + 1} / {project.images!.length}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Actions Card */}
              {/* <Card className="bg-white border-slate-200 shadow-xl rounded-2xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Sparkles size={16} className="text-primary" />
                    </div>
                    {t('portfolio.actions') || 'الإجراءات'}
                  </h3>
                </CardHeader>
                <CardContent className="p-6 space-y-3">
                  {isApproved && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/dashboard/portfolio', { state: { editProjectId: projectId } })}
                        className="w-full gap-2 bg-white border-slate-200 hover:bg-slate-50"
                      >
                        <Edit size={18} />
                        {t('portfolio.edit') || 'تعديل المشروع'}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteProject.isPending}
                        className="w-full gap-2"
                      >
                        <Trash2 size={18} />
                        {t('portfolio.delete') || 'حذف المشروع'}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/dashboard/portfolio')}
                    className="w-full gap-2 bg-slate-50 hover:bg-slate-100"
                  >
                    <ArrowLeft size={18} />
                    {t('portfolio.backToPortfolio') || 'العودة للمعرض'}
                  </Button>
                </CardContent>
              </Card> */}

              {/* Info Card */}
              <Card className="bg-white border-slate-200 shadow-xl rounded-2xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield size={16} className="text-primary" />
                    </div>
                    {t('portfolio.projectInfo') || 'معلومات المشروع'}
                  </h3>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">{t('portfolio.projectId') || 'معرف المشروع'}</p>
                    <p className="font-medium text-slate-900">#{project.id}</p>
                  </div>
                  
                  {project.status && (
                    <div className="space-y-2">
                      <p className="text-sm text-slate-600">{t('common.status') || 'الحالة'}</p>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          project.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : project.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {project.status === 'approved'
                          ? t('common.approved') || 'معتمد'
                          : project.status === 'rejected'
                          ? t('common.rejected') || 'مرفوض'
                          : t('common.pending') || 'قيد المراجعة'}
                      </span>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">{t('portfolio.totalImages') || 'إجمالي الصور'}</p>
                    <p className="font-medium text-slate-900">
                      {project.images ? project.images.length : 0} {t('portfolio.images') || 'صور'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
